import axios from 'axios';
import { getApiBaseUrl } from './api';
import type { ApiKey, CreatedApiKey } from './sdkKeysApi';

export interface RuntimeModel {
  id: string;
  label: string;
  vision: boolean;
  contextWindow: number | null;
  inputPerMillionUsd: number;
  outputPerMillionUsd: number;
}

export interface RuntimeModels {
  available: boolean;
  auto: string;
  models: RuntimeModel[];
}

export type RuntimeUsagePeriod = 'period' | '30d' | 'all';

export interface RuntimeUsage {
  available: boolean;
  period: RuntimeUsagePeriod;
  since: string | null;
  total_usd: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  by_key: { apiKeyId: string; name: string; prefix: string | null; revoked: boolean; calls: number; usd: number; lastAt: string }[];
  by_model: { model: string; label: string; calls: number; usd: number }[];
  daily: { day: string; calls: number; usd: number }[];
  page: number;
  page_size: number;
  recent: {
    at: string; apiKeyId: string | null; key_name: string; requestedModel: string | null; model: string;
    routedBy: string | null; inputTokens: number; outputTokens: number; usd: number;
  }[];
}

const base = () => `${getApiBaseUrl()}/ai-runtime`;

export const runtimeBaseUrl = () => `${getApiBaseUrl()}/runtime/v1`;

export const aiRuntimeApi = {
  async listKeys(): Promise<ApiKey[]> {
    const res = await axios.get<{ data: ApiKey[] }>(`${base()}/keys`);
    return res.data.data;
  },

  async createKey(name: string): Promise<CreatedApiKey> {
    const res = await axios.post<{ data: CreatedApiKey }>(`${base()}/keys`, { name });
    return res.data.data;
  },

  async rotateKey(keyId: string): Promise<CreatedApiKey> {
    const res = await axios.post<{ data: CreatedApiKey }>(`${base()}/keys/${keyId}/rotate`);
    return res.data.data;
  },

  async revokeKey(keyId: string): Promise<void> {
    await axios.delete(`${base()}/keys/${keyId}`);
  },

  async usage(period: RuntimeUsagePeriod, page = 1): Promise<RuntimeUsage> {
    const res = await axios.get<RuntimeUsage>(`${base()}/usage`, { params: { period, page } });
    return res.data;
  },

  async models(): Promise<RuntimeModels> {
    const res = await axios.get<RuntimeModels>(`${base()}/models`);
    return res.data;
  },
};
